import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, ReplaySubject, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CollectionOption {
    value: string | number;
    label: string;
}

export interface CollectionField {
    name: string;
    label: string;
    type: 'text' | 'textarea' | 'url' | 'date' | 'number' | 'image' | 'select' | 'password';
    required?: boolean;
    nullable?: boolean;
    hideInTable?: boolean;
    default?: string | number;
    options?: CollectionOption[];
}

export interface CollectionConfig {
    name: string;
    label: string;
    pk: string;
    defaultSort: string[];
    fields: CollectionField[];
}

export interface UploadedFile {
    id: string;
}

interface CollectionListResponse { data: CollectionConfig[]; }
interface ListResponse<T> { data: T[]; }
interface ItemResponse<T> { data: T; }
interface TokenResponse { data: { token: string }; }

const TOKEN_KEY = 'cidhum_cms_token';

@Injectable({ providedIn: 'root' })
export class AdminService {
    private previewCache = new Map<string, ReplaySubject<string | null>>();

    constructor(private http: HttpClient) {}

    get token(): string | null {
        return sessionStorage.getItem(TOKEN_KEY);
    }

    hasSession(): boolean {
        return !!this.token;
    }

    login(email: string, password: string): Observable<void> {
        return this.http.post<TokenResponse>(`${environment.cmsUrl}/api/auth/login`, { email, password }).pipe(
            map(response => sessionStorage.setItem(TOKEN_KEY, response.data.token)),
            catchError(error => this.handleError(error))
        );
    }

    logout(): void {
        sessionStorage.removeItem(TOKEN_KEY);
        this.previewCache.clear();
    }

    getCollections(): Observable<CollectionConfig[]> {
        return this.http.get<CollectionListResponse>(`${environment.cmsUrl}/api/collections`, { headers: this.authHeaders() }).pipe(
            map(response => response.data),
            catchError(error => this.handleError(error))
        );
    }

    list<T>(collection: string): Observable<T[]> {
        return this.http.get<ListResponse<T>>(`${environment.cmsUrl}/api/collections/${collection}`, {
            headers: this.authHeaders()
        }).pipe(
            map(response => response.data),
            catchError(error => this.handleError(error))
        );
    }

    create(collection: string, record: Record<string, unknown>): Observable<number> {
        return this.http.post<ItemResponse<{ id: number }>>(
            `${environment.cmsUrl}/api/collections/${collection}`,
            record,
            { headers: this.authHeaders() }
        ).pipe(
            map(response => response.data.id),
            catchError(error => this.handleError(error))
        );
    }

    update(collection: string, id: number, record: Record<string, unknown>): Observable<void> {
        return this.http.put<ItemResponse<{ id: number }>>(
            `${environment.cmsUrl}/api/collections/${collection}/${id}`,
            record,
            { headers: this.authHeaders() }
        ).pipe(
            map(() => undefined),
            catchError(error => this.handleError(error))
        );
    }

    delete(collection: string, id: number): Observable<void> {
        return this.http.delete<ItemResponse<{ id: number }>>(
            `${environment.cmsUrl}/api/collections/${collection}/${id}`,
            { headers: this.authHeaders() }
        ).pipe(
            map(() => undefined),
            catchError(error => this.handleError(error))
        );
    }

    upload(mime: string, base64: string, filename: string): Observable<UploadedFile> {
        return this.http.post<ItemResponse<UploadedFile>>(
            `${environment.cmsUrl}/api/files`,
            { mime, base64, filename },
            { headers: this.authHeaders() }
        ).pipe(
            map(response => response.data),
            catchError(error => this.handleError(error))
        );
    }

    preview(fileId: string | null): Observable<string | null> {
        if (!fileId) return of(null);
        let subject = this.previewCache.get(fileId);
        if (!subject) {
            subject = new ReplaySubject<string | null>(1);
            this.loadPreview(fileId, subject);
            this.previewCache.set(fileId, subject);
        }
        return subject.asObservable();
    }

    private loadPreview(fileId: string, target: ReplaySubject<string | null>): void {
        this.http.get(`${environment.cmsUrl}/api/files/${fileId}`, {
            headers: this.authHeaders(),
            responseType: 'blob'
        }).pipe(
            map(blob => URL.createObjectURL(blob)),
            catchError(error => this.handleError(error))
        ).subscribe({
            next: objectUrl => target.next(objectUrl),
            error: () => target.next(null)
        });
    }

    private authHeaders(): { Authorization: string } {
        return { Authorization: `Bearer ${this.token || ''}` };
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        const message = typeof error.error?.error === 'string'
            ? error.error.error
            : error.status === 401
                ? 'Sesión caducada. Inicia sesión de nuevo.'
                : 'No se pudo completar la operación. Intenta de nuevo.';
        return throwError(() => new Error(message));
    }
}